import {
    type ActionArray,
    Grid,
    GridItem,
    Input,
    Modal,
    Notification,
    RadioButton,
    Typography,
    useMediaQueryContext,
} from "@drone-js/core";
import type { HeroAssetImage } from "@drone-js/core";
import { OneOnboardingImages } from "libs/images/oneOnboarding";
import { type FunctionComponent, useState } from "react";
import { usePropertySelectionViewModelValue } from "../propertySelectionViewModelAtom";
import { useKeyDownHandler } from "../useKeyDownHandler";
import { ValidationError } from "./constants";
import { usePaintedDoorHandler } from "./usePaintedDoorHandler";

interface Props {
    open: boolean;
    onClose: () => void;
}

export const PaintedDoorModal: FunctionComponent<Props> = ({ open, onClose }) => {
    const {
        importMethodCms: {
            modalTitle,
            next,
            modalTrip,
            modalMakeMyTrip,
            modalOthers,
            modalTraveloka,
            modalYanolja,
            modalVRBO,
            modalPleaseSpecify,
            modalPlatformName,
            modalClose,
            modalComingSoon,
            modalComingSoonDescription,
            modalPleaseSelect,
            modalPleaseSpecifyPlatform,
        },
    } = usePropertySelectionViewModelValue();

    const choices = {
        "Trip.com": modalTrip,
        "MakeMyTrip.com": modalMakeMyTrip,
        "Traveloka.com": modalTraveloka,
        "Yanolja.com": modalYanolja,
        "VRBO.com": modalVRBO,
        Others: modalOthers,
    };

    const { handleSubmit, loading, completed, validationError } = usePaintedDoorHandler();

    const context = useMediaQueryContext();

    const [choice, setChoice] = useState<keyof typeof choices>();
    const [others, setOthers] = useState("");

    useKeyDownHandler(
        (e) => {
            if (e.code === "Enter" && !loading && !completed) handleSubmit(choice, others);
        },
        [handleSubmit, loading, completed],
    );

    const comingSoonHeroAssetProps: HeroAssetImage = {
        src: OneOnboardingImages.ImportMethodComingSoon,
        alt: "Coming soon",
        height: "180px",
    };

    const closeButtonProps: ActionArray = [
        {
            actionType: "button",
            hierarchy: "secondary",
            variant: "label",
            label: modalClose,
            state: loading ? "loading" : "enabled",
            onClick: onClose,
        },
    ];

    const submitButtonProps: ActionArray = [
        {
            actionType: "button",
            hierarchy: "primary",
            variant: "label",
            label: next,
            "data-element-name": "import-from-another-platform-next-button",
            onClick: () => handleSubmit(choice, others),
        },
    ];

    return (
        <Modal
            visible={open}
            onClose={onClose}
            size={context.m ? "m" : "s"}
            title={completed ? modalComingSoon : modalTitle}
            heroAsset={completed ? comingSoonHeroAssetProps : undefined}
            actions={completed ? closeButtonProps : submitButtonProps}
        >
            {!completed ? (
                <>
                    <Grid spacing="16">
                        {Object.entries(choices).map(([key, value]) => (
                            <GridItem key={`paintedDoorRadio${key}`} sm={12}>
                                <RadioButton
                                    selection={choice === key ? "selected" : "unselected"}
                                    onClick={() => setChoice(key as keyof typeof choices)}
                                >
                                    <Typography variant="body-s-strong">{value}</Typography>
                                </RadioButton>
                            </GridItem>
                        ))}
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {choice === "Others" ? (
                            <GridItem sm={12}>
                                <Input
                                    label={modalPlatformName}
                                    placeholder={modalPleaseSpecify}
                                    persistentLabel
                                    clearLabel="Clear"
                                    value={others}
                                    onChange={(e) => {
                                        setOthers(e.currentTarget.value);
                                    }}
                                    intent={validationError === ValidationError.MissingOthers ? "error" : undefined}
                                    helperText={
                                        validationError === ValidationError.MissingOthers
                                            ? modalPleaseSpecifyPlatform
                                            : undefined
                                    }
                                />
                            </GridItem>
                        ) : validationError === ValidationError.MissingChoice ? (
                            <GridItem sm={12}>
                                <Notification
                                    intent="error"
                                    title={modalPleaseSelect}
                                    asset={{
                                        id: "fill.symbol.triangle-exclamation",
                                    }}
                                />
                            </GridItem>
                        ) : null}
                    </Grid>
                    {/* janky hack to prevent modal from resizing */}
                    <p
                        style={{
                            margin: "2px",
                        }}
                    />
                </>
            ) : (
                <Typography variant="body-s" textColor="base.default">
                    {modalComingSoonDescription}
                </Typography>
            )}
        </Modal>
    );
};
